import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import CrossIcon from "../assets/icons/ProcessingQueueIcons/Close.png";
import DownIcon from "../assets/icons/ProcessingQueueIcons/DownIcon.png";
import FileIcon from "../assets/icons/ProcessingQueueIcons/FileIcon.png";
import TickIcon from "../assets/icons/icons_tick.png";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";
import api from "../api";
import {
  clearQueue,
  removeNewFile,
  setDuplicates,
  setNewFiles,
  appendNewFiles,
  setStatus,
  updateFile,
  setWorkerRunning,
  markJobConsumed,
  removeDuplicate,
  removeDuplicatesByHash,
} from "../redux/uploadQueueSlice";

const API_BASE = process.env.REACT_APP_API_URL;

export default function UploadQueue() {
  const [minimized, setMinimized] = useState(true);
  const [processingAction, setProcessingAction] = useState(false);
  const [uploadingDuplicates, setUploadingDuplicates] = useState([]);
  const [position, setPosition] = useState({ bottom: 20, right: 20 });

  const uploadState = useSelector((s) => s.uploadQueue);

  const queue = uploadState.queue || [];
  const duplicates = uploadState.duplicates || [];
  const newFiles = uploadState.files || [];

  const isQueueVisible =
    queue.length > 0 || newFiles.length > 0 || duplicates.length > 0;
  const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

  const failedToastShownRef = useRef(new Set());
  const dragStart = useRef({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragRef = useRef(null);
  const location = useLocation();
  const dispatch = useDispatch();
  const showQueue = location.pathname.startsWith("/user");

  //If new file are updated then they are received here
  // console.log("newFiles", newFiles);
  // console.log("duplicates", duplicates);

  // ------------------------------------------------
  // DUPLICATE CHECK (RUNS ONLY ONCE PER JOB)
  // ------------------------------------------------
  const checkFileDuplicates = async (files, meta, jobId) => {
    const token = localStorage.getItem("access_token");

    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      fd.append("mode", meta.mode.toLowerCase());
      fd.append("description", meta.description || "");

      const res = await api.post(
        `${API_BASE}/uploaded-files/newCheckDuplicates`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // (res.data.duplicates || []).forEach((dup) => {
      //   showErrorToast(`${dup.filename} already exists. Upload skipped.`);
      // });
      (res.data.duplicates || []).forEach((dup) => {
        showErrorToast(
          dup.toast?.message ||
            "This file already exists and cannot be uploaded.",
        );
      });

      // DUPLICATES
      //uncomment if you want the duplicate files currently we dont want it
      // const duplicatesWithFile = (res.data.duplicates || []).map((dup) => ({
      //   ...dup,
      //   file: files.find((f) => f.name === dup.originalname),
      // }));

      //uncomment if you want the duplicate files currently we dont want it
      // dispatch(setDuplicates(duplicatesWithFile));
      // setMinimized(false);

      // NEW FILES
      // const newFilesFromServer = (res.data.new_files || []).map((nf) => ({
      //   id: nf.file_hash,
      //   name: nf.originalname,
      //   file_hash: nf.file_hash,
      //   size: nf.size,
      //   file: files.find((f) => f.name === nf.originalname),
      //   status: "pending",
      //   progress: 0,
      //   uploading: false,
      //   finalized: false,
      //   mode: nf.upload_mode,
      //   description: nf.file_description,
      // }));

      const existingHashes = new Set(uploadState.files.map((f) => f.file_hash));

      const newFilesFromServer = (res.data.new_files || [])
        .filter((nf) => !existingHashes.has(nf.file_hash)) // 🔥 BLOCK DUPES
        .map((nf) => ({
          id: nf.file_hash,
          name: nf.originalname,
          file_hash: nf.file_hash,
          size: nf.size,
          file: files.find((f) => f.name === nf.originalname),
          status: "pending",
          progress: 0,
          uploading: false,
          finalized: false,
          mode: nf.upload_mode,
          description: nf.file_description,
        }));

      // dispatch(setNewFiles(newFilesFromServer));
      dispatch(appendNewFiles(newFilesFromServer));
      setMinimized(false);
      // 🔐 MARK JOB AS CONSUMED (CRITICAL)
      dispatch(markJobConsumed(jobId));
    } catch (err) {
      console.error("Duplicate check failed", err);
    }
  };

  // ------------------------------------------------
  // FILE UPLOAD (ROUTE SAFE)
  // ------------------------------------------------
  const uploadNewFiles = async (filesToUpload) => {
    const token = localStorage.getItem("access_token");
    // setMinimized(false);
    // console.log("filesToUpload", filesToUpload);

    for (const fileObj of filesToUpload) {
      // 🛑 HARD STOP — survives navigation
      // if (fileObj.uploading || fileObj.status === "success") continue;
      if (
        fileObj.uploading ||
        fileObj.status === "success" ||
        fileObj.finalized
      ) {
        continue;
      }

      try {
        dispatch(
          updateFile({
            id: fileObj.id,
            status: "uploading",
            uploading: true,
          }),
        );

        const fd = new FormData();
        fd.append("files", fileObj.file);
        fd.append("file_hash", fileObj.file_hash);
        fd.append("mode", fileObj.mode);
        fd.append("description", fileObj.description);
        const token = localStorage.getItem("access_token");

        const response = await api.post(
          `${API_BASE}/uploaded-files/newUpload`,
          fd,
          {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (e) => {
              const progress = Math.round((e.loaded * 100) / e.total);
              dispatch(updateFile({ id: fileObj.id, progress }));
            },
          },
        );

        // console.log("Response from the fast api", response);

        const fastapi = response.data.fastapi_response;
        const failedFiles = fastapi?.files_failed || [];
        const successFiles = fastapi?.files_success || [];

        // 🔍 check if THIS file failed
        const failedEntry = failedFiles.find(
          (f) => f.file === fileObj.file.name,
        );

        if (failedEntry) {
          // ❌ logical failure from backend
          dispatch(
            updateFile({
              id: fileObj.id,
              status: "failed",
              uploading: false,
              finalized: true,
              error: failedEntry.error || "Upload failed",
            }),
          );

          // 🔔 toast once
          if (!failedToastShownRef.current.has(fileObj.id)) {
            showErrorToast(
              `Upload failed: ${fileObj.file.name} — ${failedEntry.error}`,
            );
            failedToastShownRef.current.add(fileObj.id);
          }

          setTimeout(() => {
            dispatch(removeNewFile(fileObj.id));
          }, 10000);

          continue; // ⛔ IMPORTANT: skip success path
        }

        // ✅ real success
        dispatch(
          updateFile({
            id: fileObj.id,
            status: "success",
            uploading: false,
            finalized: true,
            backendData: successFiles[0] || null,
          }),
        );

        setTimeout(() => {
          dispatch(removeNewFile(fileObj.id));
        }, 5000);
      } catch (err) {
        console.error("Upload failed:", err);

        dispatch(
          updateFile({
            id: fileObj.id,
            status: "failed",
            uploading: false,
            finalized: true,
            error: err?.response?.data?.message || "Upload failed",
          }),
        );

        // ✅ SHOW TOAST HERE (guaranteed)
        if (!failedToastShownRef.current.has(fileObj.id)) {
          showErrorToast(`Upload failed: ${fileObj.name}`);
          failedToastShownRef.current.add(fileObj.id);
        }

        // ✅ remove failed after delay
        setTimeout(() => {
          dispatch(removeNewFile(fileObj.id));
        }, 10000);
      }
    }
  };

  // ------------------------------------------------
  // RUN DUPLICATE CHECK (ONCE)
  // ------------------------------------------------
  useEffect(() => {
    if (!queue.length) return;

    const job = queue[0];

    // 🔐 only block THIS job if consumed
    if (uploadState.consumedJobs[job.jobId]) return;

    if (job.type === "files") {
      checkFileDuplicates(job.items, job.meta, job.jobId);
    }
  }, [queue, uploadState.consumedJobs]);

  // ------------------------------------------------
  // UPLOAD PENDING FILES (SAFE ON NAVIGATION)
  // ------------------------------------------------
  useEffect(() => {
    if (uploadState.workerRunning) return;
    if (!newFiles.length) return;

    const pendingFiles = newFiles.filter(
      (f) => f.status === "pending" && !f.uploading,
    );

    if (!pendingFiles.length) return;

    dispatch(setWorkerRunning(true));

    (async () => {
      await uploadNewFiles(pendingFiles);
      dispatch(setWorkerRunning(false));
    })();
  }, [newFiles, uploadState.workerRunning]);

  // -----------------------------
  // MOVE WINDOW
  // -----------------------------
  const handleMouseDown = (e) => {
    if (!minimized) return;
    if (e.target.closest("button")) return;

    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!dragging.current) return;

    const dy = e.clientY - dragStart.current.y;
    setPosition((prev) => {
      const boxHeight = dragRef.current?.offsetHeight || 40;
      const maxBottom = window.innerHeight - boxHeight - 10;
      const minBottom = 10;

      const newBottom = clamp(prev.bottom - dy, minBottom, maxBottom);

      return { ...prev, bottom: newBottom };
    });

    dragStart.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    dragging.current = false;
    document.body.style.userSelect = "auto";
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const uploadingList = useMemo(() => {
    // If backend gave new files → show them
    if (newFiles?.length > 0) return newFiles;

    // If user continued duplicates → show them
    if (uploadingDuplicates?.length > 0) return uploadingDuplicates;

    // Otherwise → nothing should show
    return [];
  }, [newFiles, uploadingDuplicates]);

  // console.log("uploadingList", uploadingList);

  // const saveDuplicateToDB = async (fileObj, file) => {
  //   if (!fileObj || !file) {
  //     console.error("fileObj or file missing", { fileObj, file });
  //     return;
  //   }

  //   const formData = new FormData();
  //   formData.append("files", file);
  //   formData.append("file_hash", fileObj.file_hash);
  //   formData.append("originalname", fileObj.originalname);
  //   formData.append("mode", fileObj.upload_mode || fileObj.mode);
  //   formData.append("description", fileObj.description || "");
  //   const token = localStorage.getItem("access_token");

  //   try {
  //     const res = await api.post(
  //       `${API_BASE}/uploaded-files/saveDuplicateFiles`,
  //       formData,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "multipart/form-data",
  //         },
  //       },
  //     );

  //     return res.data;
  //   } catch (err) {
  //     console.error("Failed to save duplicate:", err);
  //     throw err;
  //   }
  // };
  // const handleContinueDuplicate = async (fileObj) => {
  //   if (!fileObj?.file) return;

  //   try {
  //     setProcessingAction(true);

  //     // 1️⃣ Upload duplicate
  //     await saveDuplicateToDB(fileObj, fileObj.file);

  //     // 2️⃣ Remove from duplicate queue
  //     dispatch(removeDuplicate(fileObj.file_hash));

  //     showSuccessToast(`"${fileObj.originalname}" uploaded successfully`);
  //   } catch (err) {
  //     showErrorToast(`Failed to upload "${fileObj.originalname}"`);
  //   } finally {
  //     setProcessingAction(false);
  //   }
  // };

  // const handleContinueAll = async () => {
  //   if (!duplicates.length) return;

  //   setProcessingAction(true);

  //   const succeeded = [];
  //   const failed = [];

  //   try {
  //     for (const fileObj of duplicates) {
  //       if (!fileObj.file) continue;

  //       try {
  //         await saveDuplicateToDB(fileObj, fileObj.file);
  //         succeeded.push(fileObj.file_hash);
  //       } catch {
  //         failed.push(fileObj.file_hash);
  //       }
  //     }

  //     // 🔥 remove ONLY successful ones (REDUX owns state)
  //     if (succeeded.length > 0) {
  //       dispatch(removeDuplicatesByHash(succeeded));
  //     }

  //     if (failed.length === 0) {
  //       showSuccessToast("All duplicate files uploaded successfully");
  //     } else {
  //       showErrorToast(
  //         `${failed.length} duplicate file(s) failed. You can retry.`,
  //       );
  //     }
  //   } finally {
  //     setProcessingAction(false);
  //   }
  // };

  const totalCount = duplicates.length + uploadingList.length;

  // 🚫 Hide queue completely when empty
  if (!showQueue || !isQueueVisible) {
    return null;
  }

  return (
    <>
      {showQueue && (
        <div
          className={`upload-queue-box position-fixed shadow-lg refresh-prompts ${
            minimized ? "queue-minimized" : ""
          }`}
          ref={dragRef}
          onMouseDown={handleMouseDown}
          style={{
            bottom: position.bottom,
            right: position.right,
            cursor: "grab",
          }}
        >
          {/* HEADER */}
          <div className="queue-header d-flex justify-content-between align-items-center p-2 ak-text-md">
            {totalCount === 0 ? (
              <span>No Files Uploading</span>
            ) : (
              <span>Uploading {totalCount} Files</span>
            )}

            <div className="d-flex gap-1">
              <button
                className="custom-btn p-1 rounded-1"
                onClick={() => setMinimized((p) => !p)}
              >
                <img src={DownIcon} width={14} className="icon-image3" />
              </button>
            </div>
          </div>

          {/* CONTENT */}
          {!minimized && (
            <div className="queue-content">
              {/* DUPLICATES FILES */}
              {/* {duplicates.length > 0 && (
                <div className="px-2">
                  <div className="d-flex justify-content-between align-items-center p-2 pb-1 overflow-hidden ">
                    <h6 className="m-0 refresh-prompts queue-section-title">
                      Duplicate Files
                    </h6>

                    <button
                      type="button"
                      className="bg-secondary continue-btn"
                      onClick={handleContinueAll}
                      disabled={processingAction || duplicates.length === 0}
                    >
                      Continue All
                    </button>
                  </div>

                  {duplicates.length === 0 && (
                    <div className="px-3 pb-2 queuetext">No duplicates</div>
                  )}

                  {duplicates.map((d, i) => (
                    <div
                      key={i}
                      className="p-2 border mb-1 d-flex align-items-center w-100 ak-text-sm overflow-hidden gap-2"
                    >
                      <div
                        className="queue-filename d-flex align-items-center gap-2 flex-grow-0"
                        style={{ width: "55%" }}
                      >
                        <img src={FileIcon} width={14} className="icon-image" />

                        <span className="text-truncate d-block">
                          {d.originalname}
                        </span>
                      </div>

                      <div className="d-flex flex-shrink-0 align-items-center">
                        <div className="me-1">
                          {d.upload_mode && (
                            <span
                              className={`badge text-capitalize ${
                                d.upload_mode === "enterprise"
                                  ? "bg-primary"
                                  : "bg-secondary"
                              }`}
                              style={{ fontSize: "10px", whiteSpace: "nowrap" }}
                            >
                              {d.upload_mode}
                            </span>
                          )}
                        </div>

                        <button
                          className="btn btn-sm me-1 border"
                          onClick={() => dispatch(removeDuplicate(d.file_hash))}
                          disabled={processingAction}
                        >
                          <img
                            src={CrossIcon}
                            width={14}
                            className="icon-image"
                          />
                        </button>

                        <button
                          className="btn btn-sm border"
                          onClick={() => handleContinueDuplicate(d)}
                          disabled={processingAction}
                        >
                          <img
                            src={TickIcon}
                            width={14}
                            className="icon-image"
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )} */}

              {/* NEW FILES */}
              {uploadingList.length > 0 && (
                <h6 className="p-2 pt-3 queue-section-title">
                  Uploading Files
                </h6>
              )}

              {uploadingList.map((f) => (
                <div
                  key={f.id}
                  className="queue-item p-2 border mx-2 mb-1 d-flex justify-content-between align-items-center overflow-hidden"
                >
                  <div className="d-flex gap-2" style={{ width: "65%" }}>
                    <img src={FileIcon} width={16} className="icon-image" />
                    <span className="queue-filename text-truncate">
                      {f.name}
                    </span>
                  </div>

                  <div className="d-flex gap-1">
                    <div>
                      {f.mode && (
                        <span
                          className={`badge text-capitalize ${
                            f.mode === "enterprise"
                              ? "bg-primary"
                              : "bg-secondary"
                          }`}
                          style={{ fontSize: "10px", whiteSpace: "nowrap" }}
                        >
                          {f.mode}
                        </span>
                      )}
                    </div>
                    <div className="queue-status d-flex justify-content-center">
                      {/* Uploading */}
                      {f.status === "uploading" && (
                        <div
                          className="spinner-border text-success spinner-sm"
                          style={{ width: "14px", height: "14px" }}
                        ></div>
                      )}

                      {/* Pending */}
                      {f.status === "pending" && (
                        <div
                          className="spinner-border text-primary spinner-sm"
                          style={{ width: "14px", height: "14px" }}
                        ></div>
                      )}

                      {/* Success */}
                      {f.status === "success" && (
                        <img src={TickIcon} width={18} className="icon-image" />
                      )}

                      {/* Failed */}
                      {f.status === "failed" && (
                        <span className="text-danger">Failed</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
